import { GoHome } from "react-icons/go";
import { LuBarChart } from "react-icons/lu";
import { VscGitPullRequestGoToChanges } from "react-icons/vsc";
import { IoMdCodeWorking } from "react-icons/io";
import { IoCodeSlashOutline } from "react-icons/io5";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { MdOutlineNotificationsActive } from "react-icons/md";
import { GrCurrency } from "react-icons/gr";
import { useLocation } from "react-router-dom";
import CryptoJS from "crypto-js";
import { createContext, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import { FaRegCalendarAlt } from "react-icons/fa";
import { message, Modal, Upload } from "antd";
import media from "../media";
import SubmitButton from "../components/atoms/Buttons/SubmitButton";
import { downloadMonthWiseExpenseRequestReportThunk } from "../store/thunks/downloadMonthWiseExpenseThunk";
import { store } from "../store";

export const downloadMonthWiseExpenseRequestReport = async (
  storeRole,
  storeToken,
  storeLIUId,
  storeAccessTokenExpiresOn,
  currentPath,
  status,
  hasSearchValue,
  setShowLoader
) => {
  try {
    setShowLoader(true);

    const exportColumns = [
      { key: "empName", path: "empName" },
      { key: "empID", path: "empID" },
      { key: "natureOfExpense", path: "natureOfExpense" },
      { key: "vendorName", path: "businessPurpose" },
      { key: "projectCodeID", path: "projectCodeID" },
      { key: "companyName", path: "" },
      { key: "expensePeriod", path: "" },
      { key: "month", path: "expensePeriodFromDate" },
      { key: "departmentID", path: "departmentID" },
      { key: "expenseCodeID", path: "" },
      { key: "currencyID", path: "" },
      { key: "recharge", path: "recharge" },
      { key: "cost", path: "" },
      { key: "totalReimbursement", path: "totalReimbursement" },
      { key: "approvers", path: "" },
      { key: "adminApprover", path: "" },
      { key: "status", path: "status" },
      { key: "paymentPaidOn", path: "paymentPaidOn" },
      { key: "modeOfPayment", path: "modeOfPayment" },
    ];

    let startRange = "";
    let endRange = "";

    const startMonthInput = document.querySelector('input[date-range="start"]');
    if (startMonthInput?.value) {
      startRange = `${new Date(startMonthInput.value).getFullYear()}-${String(
        new Date(startMonthInput.value).getMonth() + 1
      ).padStart(2, "0")}`;
    }

    const endMonthInput = document.querySelector('input[date-range="end"]');
    if (endMonthInput?.value) {
      endRange = `${new Date(endMonthInput.value).getFullYear()}-${String(
        new Date(endMonthInput.value).getMonth() + 1
      ).padStart(2, "0")}`;
    }

    const payload = {
      role: storeRole,
      month: startRange && endRange ? { startRange, endRange } : "",
      exportOf: "expenseRequest",
      exportColumns,
      status,
      lIUId: storeLIUId,
      searchKeyword: hasSearchValue || "",
      isBlobRequest: true,
      downloadFileName: currentPath,
      currentPath,
      storeAccessTokenExpiresOn,
    };

    // Dispatch thunk (thunk will handle Blob download internally)
    await store
      .dispatch(downloadMonthWiseExpenseRequestReportThunk(payload))
      .unwrap();
  } catch (error) {
    console.error("Error downloading file:", error);
    message.error("Download failed");
  } finally {
    setShowLoader(false);
  }
};

export const flattenDataForExcel = (data) => {
  // Define the exact column order
  const columnOrder = [
    "empName",
    "empID",
    "natureOfExpense",
    "businessPurpose",
    "projectCodeID",
    "companyName",
    "expensePeriod",
    "month",
    "departmentID",
    "expenseCodeID",
    "currencyID",
    "recharge",
    "cost",
    "totalReimbursement",
    "approvers",
    "adminApprover",
    "status",
    "paymentPaidOn",
  ];
  const result = [];
  data.forEach((item) => {
    const { key, id, children, ...filteredData } = item;

    // Reorder columns for the parent
    // Reorder parent row according to columnOrder
    const reorderedParent = columnOrder.reduce((acc, key) => {
      acc[key] = filteredData[key] || "-"; // Use empty string if the field is missing
      return acc;
    }, {});

    // Add the parent row with all values
    result.push(reorderedParent);

    // Add children rows with only relevant columns
    if (children) {
      children.forEach((child) => {
        const reorderedChild = columnOrder.reduce((acc, key) => {
          acc[key] = child[key] || ""; // Ensure child fields follow the same order
          return acc;
        }, {});
        result.push(reorderedChild);
      });
    }
  });
  return result;
};

export const sortData = (data) => {
  return data.sort((a, b) => {
    if (a.label < b.label) {
      return -1; // a comes before b
    }
    if (a.label > b.label) {
      return 1; // a comes after b
    }
    return 0; // a and b are equal
  });
};

export const paginationOptions = (storeTotalCount) => {
  if (typeof storeTotalCount !== "number" || storeTotalCount <= 0) {
    // Fallback to default pagination options if storeTotalCount is invalid
    return ["10", "20", "50", "100"];
  }
  return storeTotalCount > 100
    ? ["10", "20", "50", "100", `${storeTotalCount}`]
    : storeTotalCount < 10
    ? [`${storeTotalCount}`, "10", "20", "50", "100"]
    : (() => {
        const baseOptions = ["10", "20", "50", "100"];
        if (!baseOptions.includes(`${storeTotalCount}`)) {
          baseOptions.push(`${storeTotalCount}`);
        }
        return baseOptions.sort((a, b) => Number(a) - Number(b));
      })();
};

export const sideMenu = {
  SUPERADMIN: [
    { "/dashboard": { name: "Dashboard", icon: media.sidebarDashboard.svg } },
    {
      "/list-of-expense": {
        name: "List of Expense",
        icon: media.sidebarList.svg,
      },
    },
    {
      "/pending-requests": {
        name: "Pending Requests",
        icon: media.sidebarPendigRequest.svg,
        showCount: "no",
      },
    },
    {
      "/project-code": {
        name: "Project Code",
        icon: media.sidebarProjectCode.svg,
      },
    },
    {
      "/expense-code": {
        name: "Expense Code",
        icon: media.sidebarExpenseCode.svg,
      },
    },
    {
      "/departments": {
        name: "Departments",
        icon: media.sidebarDepartments.svg,
      },
    },
    {
      "/currency": {
        name: "Currency",
        icon: media.sidebarCurrency.svg,
      },
    },
    { "/roles": { name: "Roles", icon: media.sidebarRoles.svg } },
    {
      "/permissions": {
        name: "Permissions",
        icon: media.sidebarPermissions.svg,
      },
    },
    { "/users": { name: "Users", icon: media.sidebarUser.svg } },
    { "/vendors": { name: "Vendors", icon: media.sidebarUser.svg } },
    {
      "/notifications": {
        name: "Notifications",
        icon: media.sidebarNotification.svg,
      },
    },
    { "/settings": { name: "Settings", icon: media.sidebarSettings.svg } },
    {
      "/expense-calendar-control": {
        name: "Expense Calendar Control",
        icon: media.sidebarCalendar.svg,
      },
    },
  ],
  ADMIN: [
    { "/dashboard": { name: "Dashboard", icon: media.sidebarDashboard.svg } },
    {
      "/list-of-expense": {
        name: "List of Expense",
        icon: media.sidebarList.svg,
      },
    },
    {
      "/pending-requests": {
        name: "Pending Requests",
        icon: media.sidebarPendigRequest.svg,
        showCount: "no",
      },
    },
    {
      "/project-code": {
        name: "Project Code",
        icon: media.sidebarProjectCode.svg,
      },
    },
    {
      "/expense-code": {
        name: "Expense Code",
        icon: media.sidebarExpenseCode.svg,
      },
    },
    {
      "/departments": {
        name: "Departments",
        icon: media.sidebarDepartments.svg,
      },
    },
    {
      "/currency": {
        name: "Currency",
        icon: media.sidebarCurrency.svg,
      },
    },
    { "/vendors": { name: "Vendors", icon: media.sidebarUser.svg } },
    {
      "/notifications": {
        name: "Notifications",
        icon: media.sidebarNotification.svg,
      },
    },
    {
      "/expense-calendar-control": {
        name: "Expense Calendar Control",
        icon: media.sidebarCalendar.svg,
      },
    },
  ],
  APPROVER: [
    { "/dashboard": { name: "Dashboard", icon: media.sidebarDashboard.svg } },
    {
      "/list-of-expense": {
        name: "List of Expense",
        icon: media.sidebarList.svg,
      },
    },
    {
      "/pending-requests": {
        name: "Pending Requests",
        icon: media.sidebarPendigRequest.svg,
        showCount: "no",
      },
    },
    {
      "/project-code": {
        name: "Project Code",
        icon: media.sidebarProjectCode.svg,
      },
    },
    {
      "/notifications": {
        name: "Notifications",
        icon: media.sidebarNotification.svg,
      },
    },
  ],
  EMPLOYEE: [
    { "/dashboard": { name: "Dashboard", icon: media.sidebarDashboard.svg } },
    {
      "/list-of-expense": {
        name: "List of Expense",
        icon: media.sidebarList.svg,
      },
    },
    {
      "/project-code": {
        name: "Project Code",
        icon: media.sidebarProjectCode.svg,
      },
    },
    {
      "/notifications": {
        name: "Notifications",
        icon: media.sidebarNotification.svg,
      },
    },
  ],
};

/*Custom hook to call currentPath*/
export default function useCurrentPath() {
  const location = useLocation();
  return location.pathname.replace("/", "");
}

// Define a secret key for encryption/decryption
const SECRET_KEY = "@ex#mgtSys"; // Make sure to use a strong key

export const encryptData = (data) => {
  try {
    return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
  } catch (error) {
    console.error("Encryption failed:", error);
    return null;
  }
};

export const decryptData = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    return bytes && JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    console.error("Decryption failed:", error);
    return null;
  }
};

const MsalContext = createContext();

export const MsalProvider = ({ children }) => {
  const msalInstance = useMsal();

  return (
    <MsalContext.Provider value={msalInstance}>{children}</MsalContext.Provider>
  );
};

export const useMsalInstance = () => {
  return useContext(MsalContext);
};

export const handleDelete = ({
  selectedRowKeys,
  dispatch,
  deleteThunk,
  route,
  actionName,
  onSuccess = () => {},
  onError = () => {},
}) => {
  if (!selectedRowKeys.length) return;

  Modal.confirm({
    title: `Are you sure you want to delete the selected ${actionName}(s)?`,
    //icon: null, // optional, remove default warning icon
    closable: true,
    maskClosable: true,

    footer: [
      <div className="d-flex justify-content-end" style={{ gap: "6px" }}>
        <SubmitButton
          ky="no"
          type="primary"
          submitName="No"
          className="btn px-5 py-2 defaultbutton rounded-lg"
          onClick={() => Modal.destroyAll()}
        />
        <SubmitButton
          ky="yes"
          type="primary"
          htmlType="submit"
          submitName="Yes"
          className="btn px-5 py-2 defaultbutton rounded-lg"
          onClick={() => {
            dispatch(
              deleteThunk({
                route,
                action: actionName,
                ids: selectedRowKeys,
              })
            )
              .unwrap()
              .then(() => {
                message.success(
                  `Selected ${actionName}(s) deleted successfully`
                );
                onSuccess();
                Modal.destroyAll(); // close modal
              })
              .catch(() => {
                message.error(`Failed to delete ${actionName}(s)`);
                onError();
                Modal.destroyAll();
              });
          }}
        />
      </div>,
    ],
  });
};

export const formateDateTime1 = (text) => {
  // Parse the original date string: "02-12-24 09:35 AM"
  const [datePart, timePart] = text?.split(" "); // ["02-12-24", "09:35", "AM"]

  // Split date part
  const [day, month, year] = datePart.split("-");

  // Convert to full year, assuming 20xx for 2-digit year
  const fullYear = parseInt(year, 10) < 50 ? "20" + year : "19" + year;

  // Create Date object
  const dateObj = new Date(
    `${fullYear}-${month}-${day} ${timePart} ${text?.split(" ")[2]}`
  );

  // Format date
  const formattedDate = dateObj.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  // Format time (keeping AM/PM)
  const formattedTime = dateObj
    .toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace(".", "."); // optional fix for extra dot

  return (
    <>
      <div>{formattedDate}</div>
      <small>{formattedTime}</small>
    </>
  );
};

export const formateDateTime = (text) => {
  if (!text) return "";

  let dateObj;

  if (text.includes("T")) {
    // Parse ISO *as UTC* (keeps exact time)
    dateObj = new Date(text);

    if (isNaN(dateObj.getTime())) return "";

    const [datePart, timePart] = text.split("T");
    const [year, month, day] = datePart.split("-");
    const timeOnly = timePart.split(".")[0]; // remove milliseconds

    const formattedDate = `${day} ${new Date(text).toLocaleString("en-US", {
      month: "short",
    })} ${year}`;

    return (
      <>
        <div>{formattedDate}</div>
        <small>{timeOnly}</small>
      </>
    );
  }

  // 🔹 OLD FORMAT
  const [datePart, time, ampm] = text.split(" ");
  const [day, month, year] = datePart.split("-");
  const fullYear = parseInt(year) < 50 ? `20${year}` : `19${year}`;

  const dateObj2 = new Date(`${fullYear}-${month}-${day} ${time} ${ampm}`);
  if (isNaN(dateObj2.getTime())) return "";

  const formattedDate2 = dateObj2.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime2 = dateObj2.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <>
      <div>{formattedDate2}</div>
      <small>{formattedTime2}</small>
    </>
  );
};

export const decrypt = (encryptedData, encryptionKey) => {
  try {
    // Decode the base64 encrypted data into a byte array
    const combined = CryptoJS.enc.Base64.parse(encryptedData);

    // Extract salt (first 16 bytes)
    const salt = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4));

    // Extract IV (next 16 bytes)
    const iv = CryptoJS.lib.WordArray.create(combined.words.slice(4, 8));

    // The remaining part is the encrypted content
    const encrypted = CryptoJS.lib.WordArray.create(combined.words.slice(8));

    // Derive the secret key using PBKDF2 with the provided salt
    const secretKey = deriveKey(encryptionKey, salt);

    // Decrypt the data using AES with the derived key and IV
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: encrypted },
      secretKey,
      {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7, // Ensure proper padding
      }
    );

    // Return the decrypted data as a UTF-8 string
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

    // Check if decryption is successful
    if (!decryptedText) {
      throw new Error("Decrypted data is empty or invalid UTF-8.");
    }

    return decryptedText;
  } catch (error) {
    console.error("Decryption error:", error.message);
    return null;
  }
};

export const deriveKey = (password, salt) => {
  return CryptoJS.PBKDF2(password, salt, {
    keySize: 256 / 32,
    iterations: 65536,
  });
};

export const listOfExpenseTabItems = (storeRole) => [
  {
    label: "All",
    values: "All",
    show: true,
    onClickValues: "ALL",
  },
  {
    label: process.env.REACT_APP_MY_REQUESTS,
    values: process.env.REACT_APP_MY_REQUESTS,
    show: storeRole !== process.env.REACT_APP_EROLE,
    onClickValues: [process.env.REACT_APP_MY_REQUESTS?.toUpperCase()],
  },
  {
    label: process.env.REACT_APP_APPROVED_STATUS,
    values: process.env.REACT_APP_APPROVED_STATUS,
    show: true,
    onClickValues: [process.env.REACT_APP_APPROVED_STATUS?.toUpperCase()],
  },
  {
    label: process.env.REACT_APP_CANCELED_STATUS,
    values: process.env.REACT_APP_CANCELED_STATUS,
    show: true,
    onClickValues: [process.env.REACT_APP_CANCELED_STATUS?.toUpperCase()],
  },
  {
    label: process.env.REACT_APP_DRAFT_STATUS,
    values: process.env.REACT_APP_DRAFT_STATUS,
    show: true,
    onClickValues: [process.env.REACT_APP_DRAFT_STATUS?.toUpperCase()],
  },
  {
    label: process.env.REACT_APP_TAB_PAYMENT_PENDING_STATUS,
    values: process.env.REACT_APP_TAB_PAYMENT_PENDING_STATUS,
    show: true,
    onClickValues: [
      process.env.REACT_APP_TAB_PAYMENT_PENDING_STATUS?.toUpperCase(),
    ],
  },
  {
    label: process.env.REACT_APP_PENDING_STATUS,
    values: process.env.REACT_APP_PENDING_STATUS,
    show: true,
    onClickValues: [
      process.env.REACT_APP_PENDING_STATUS?.toUpperCase(),
      process.env.REACT_APP_NORESPONSE_STATUS?.replace(" ", "")?.toUpperCase(),
      process.env.REACT_APP_RESUBMITTED_STATUS?.toUpperCase(),
      process.env.REACT_APP_SEEK_ACK_STATUS?.replace(" ", "")?.toUpperCase(),
      process.env.REACT_APP_ACK_REC_STATUS?.replace(" ", "")?.toUpperCase(),
    ],
  },
  {
    label: process.env.REACT_APP_SENTBACK_STATUS,
    values: process.env.REACT_APP_SENTBACK_STATUS,
    show: true,
    onClickValues: [process.env.REACT_APP_SENTBACK_STATUS?.toUpperCase()],
  },
];

export const handleGlSearch = (dispatch, thunk, route, searchValue) => {
  dispatch(
    thunk({
      route,
      search: searchValue,
      pagination: {
        current: 1,
        pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
      },
    })
  );
};

export const getTimeAgo = (timestamp) => {
  if (!timestamp) return "";

  const [datePart, timePart, meridian] = timestamp.split(" ");
  const [day, month, year] = datePart.split("-").map(Number);
  let [hours, minutes] = timePart.split(":").map(Number);

  // Convert 12-hour time to 24-hour
  if (meridian?.toLowerCase() === "pm" && hours < 12) hours += 12;
  if (meridian?.toLowerCase() === "am" && hours === 12) hours = 0;

  // Construct date (20yy)
  const fullYear = year < 100 ? 2000 + year : year;
  const created = new Date(fullYear, month - 1, day, hours, minutes);

  if (isNaN(created)) return "Not Processed";

  const now = new Date();
  const diffMs = now - created;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? "s" : ""} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  if (diffDay < 30)
    return `${Math.floor(diffDay / 7)} week${diffDay >= 14 ? "s" : ""} ago`;

  return created.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const truncateWords = (html, wordLimit = 10) => {
  // 1. Remove <style> blocks completely
  let clean = html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");

  // 2. Remove all other HTML tags
  clean = clean.replace(/<[^>]*>/g, " ");

  // 3. Normalize spaces
  clean = clean.replace(/\s+/g, " ").trim();

  // 4. Split by space and take the first 'wordLimit' words
  const words = clean.split(" ");
  const truncated = words.slice(0, wordLimit).join(" ");

  // 5. Add ellipsis if original text was longer
  return words.length > wordLimit ? truncated + "..." : truncated;
};

export const draggerProps = ({ setLoading, handleUpload }) => ({
  name: "file",
  multiple: false, // set true if multiple allowed
  accept: ".png,.jpg,.jpeg,.pdf", //,.doc,.docx
  beforeUpload(file, newFileList) {
    const isAllowed = [
      "image/png",
      "image/jpeg",
      "application/pdf",
      //"application/msword",
      //"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ].includes(file.type);

    if (newFileList.length > 1) {
      message.error("Only one file is allowed to upload!");
      return Upload.LIST_IGNORE;
    }
    if (!isAllowed) {
      message.error("File type not allowed");
      return Upload.LIST_IGNORE;
    }

    if (file.size / 1024 / 1024 > 4) {
      message.error("File size must be under 4MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  },
  onChange(info) {
    if (info.file.status === "done") {
      setLoading(false);
      message.success(`${info.file.name} uploaded successfully`);
    } else if (info.file.status === "error") {
      setLoading(false);
      message.error(`${info.file.name} upload failed`);
    }
  },
  onDrop(e) {
    // ✅ 4️⃣ Handles drag-and-drop specifically
    if (e.dataTransfer.files.length > 1) {
      message.error("Only one file can be uploaded at a time.");
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  },
  //showUploadList: true, // file preview list
  showUploadList: {
    showRemoveIcon: false,
    showPreviewIcon: true,
    showDownloadIcon: false,
    removeIcon: <></>, // optional
    showProgress: false, // disables progress/loading indicator
  },
});

export const getRateByDate = (currencyRates, currencyID, date) => {
  const history = currencyRates[currencyID] || [];
  if (!history.length || !date) return null;

  const selectedDate = new Date(date);

  // Sort ascending for clarity
  const sorted = [...history].sort(
    (a, b) => new Date(a.effectiveDateTime) - new Date(b.effectiveDateTime)
  );

  // Filter rates that are effective <= selected date
  const applicableRates = sorted.filter(
    (entry) => new Date(entry.effectiveDateTime) <= selectedDate
  );

  if (applicableRates.length > 0) {
    // Pick the latest applicable rate
    return applicableRates[applicableRates.length - 1];
  }

  // 🟡 If no past rates exist, fallback to the earliest rate available
  return sorted[0];
};

export const deFaultHandlePaginationChange = (
  list,
  page,
  pageSize,
  totalCount
) => {
  if (pageSize === totalCount) {
    list("pagination", { page: 1, pageSize: totalCount });
  } else {
    list("pagination", { current: page, pageSize });
  }
};

export const pagConfiguration = (
  pagination,
  totalCount,
  handlePaginationChange
) => {
  return {
    current: pagination?.current || 1,
    pageSize:
      pagination?.pageSize || process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
    total: totalCount,
    pageSizeOptions: paginationOptions(totalCount),
    showSizeChanger: true,
    onChange: handlePaginationChange,
    onShowSizeChange: handlePaginationChange,
  };
};
